import type {
  Abi,
  Account,
  Address,
  Chain,
  Client,
  Hex,
  TransactionReceipt,
  TransactionRequestEIP1559,
  Transport
} from 'viem'

import type { GasEstimateError } from '../../utils/gas-estimation.utils'

////
// External types
////

type ActionFlowLog = {
  readonly address: Address
  readonly data: Hex
  readonly topics: readonly Hex[]
}

export type ActionFlowReceipt = {
  readonly transactionHash: Hex
  readonly blockHash: Hex
  readonly blockNumber: bigint
  readonly gasUsed: bigint
  readonly status: 'success' | 'reverted'
  readonly logs: readonly ActionFlowLog[]
}

export type ActionFlowTxGas = {
  readonly gasLimitSource: 'app_buffered' | 'builder' | 'wallet' | 'estimate_failed'
  readonly gasEstimate?: bigint
  readonly gasLimit?: bigint
  readonly gasEstimateError?: GasEstimateError
}

export type ActionFlowStepSettlement =
  | {
      readonly submissionId: string
      readonly stepIndex: number
      readonly status: 'success'
      readonly receipt: ActionFlowReceipt
      readonly gas?: ActionFlowTxGas
    }
  | {
      readonly submissionId: string
      readonly stepIndex: number
      readonly status: 'reverted'
      readonly receipt: ActionFlowReceipt
      readonly gas?: ActionFlowTxGas
      readonly error: Error
    }
  | {
      readonly submissionId: string
      readonly stepIndex: number
      readonly status: 'failed'
      readonly error: Error
    }

export type ActionFlowResult = {
  readonly hash: Hex
  readonly receipt: ActionFlowReceipt
  // The flow's chain (flows are single-chain by contract), NOT the wallet's
  // current chain — so consumers keying reads off the result (e.g. indexer
  // sync waits) can't be repointed by a mid-flow network switch.
  readonly chainId: number
}

// Sign triggers request on the client which allows for storing the signature for use within an action flow
// Could be raw message or typed data
export type SignatureRequest = {
  readonly label: string
  readonly sign: (client: Client<Transport, Chain, Account>) => Promise<Hex>
}

/**
 * Context handed to `CallRequest.before` and `CallRequest.after` callbacks.
 *
 * - `receipt` — for `before`, the prior call's receipt (or `null` on the first
 *   call); for `after`, this call's receipt. `after` callbacks see
 *   `null` only on the engine's null-receipt fallback path (publicClient
 *   failed to fetch the receipt that the wallet already confirmed); the
 *   canonical `waitForConfirmations` builder short-circuits in that case.
 * - `publicClient` — read-only chain access bound to the flow's chain.
 * - `chainId` — convenience copy of the flow's chain id.
 * - `signal` — forwarded from the execution-loop mutex; aborts on flow cancel
 *   or unmount. Callbacks **must** check `signal.aborted` between awaits and
 *   forward `signal` to any awaitable they call. The engine cannot force a
 *   hung callback to stop — if a callback ignores `signal`, `reset()` will
 *   fail to advance the engine until the callback resolves on its own.
 */
export type WaitContext = {
  readonly receipt: TransactionReceipt | null
  readonly publicClient: Client
  readonly chainId: number
  readonly signal: AbortSignal
}

/**
 * The concrete call a `CallRequest` resolves to via `getCall()`: a viem
 * EIP-1559 request with `to`/`data` guaranteed present. This is what the engine
 * estimates, suffixes for attribution, and submits.
 */
export type ResolvedCall = TransactionRequestEIP1559 & {
  to: Address
  data: Hex
}

/**
 * A single call in an action flow.
 *
 * `before` and `after` are optional callbacks the engine awaits around the
 * call's wallet popup. Each callback both **gates time** and **breaks the
 * EIP-5792 atomic batch** at its boundary — there is no separate way to
 * declare a batch break, and there is no way to declare a wait without
 * breaking the batch. On non-EIP-5792 wallets the grouping rule is a no-op
 * (every call is already its own `singleTx`).
 *
 * Authoring rules (see ADR-2026-05-07):
 *
 * 1. **Pick the call that owns the requirement.**
 *    - If the wait is _"this call is ready when X"_ → put it on `before` of
 *      that call. Example: approve → transfer, where transfer's `before`
 *      asserts allowance is on chain before transfer signs.
 *    - If the wait is _"this call isn't done from the next call's perspective
 *      until X"_ → put it on `after` of the producing call. Example: deploy →
 *      use-deployed-contract, where deploy's `after` waits for the wallet RPC
 *      to reflect the deploy block. When in doubt, prefer `after` on the
 *      producing call.
 * 2. **Never declare a wait on both sides of the same boundary.** Setting
 *    `after` on call N _and_ `before` on call N+1 is double-wait — ambiguous
 *    to readers and pays the wait twice. The engine errors at flow
 *    construction.
 * 3. **`before` on the first call runs before the first prompt.** It receives
 *    `receipt: null` and can implement a pre-send gate that does not depend on
 *    a prior call.
 * 4. **Use the receipt the engine hands you.** `WaitContext.receipt` is
 *    always the natural neighbour. Don't reach for unrelated receipts via
 *    `publicClient.getTransactionReceipt`.
 * 5. **Callbacks must be pure with respect to host state.** A flow retry
 *    re-invokes callbacks from scratch. Closing over module-level state,
 *    dispatching into reducers, or mutating React refs from inside a callback
 *    makes retries non-deterministic. The only legitimate side effect is
 *    _waiting_.
 * 6. **Callbacks must self-bound and honour `signal`.** The engine wraps
 *    callbacks in no wall-clock timer, so one that `await`s something
 *    without an internal deadline hangs the flow. Set a deadline and throw
 *    on expiry, or compose a builder that does — `waitForConfirmations(n)`
 *    is the reference implementation. Throws surface through the engine's
 *    `execution-error` path.
 */
export type CallRequest = {
  readonly label: string
  /** Optional gas buffer as a multiplier or function of the estimate. */
  readonly gasBuffer?: number | ((gasEstimate: bigint) => bigint)
  readonly getCall: () => ResolvedCall
  /**
   * ABI(s) whose custom errors should be used to decode this call's revert
   * reason. When set, an on-chain revert of the step containing this call is
   * replayed via `publicClient.call` at the mined block and the decoded
   * custom-error name is surfaced on the reverted state as `errorName`.
   */
  readonly errorAbi?: Abi
  /**
   * Awaited before this call's wallet popup. Receives the prior call's
   * receipt. Presence of `before` makes this call the first call of a new
   * atomic-batch unit.
   *
   * On the first call of a flow it runs before the first prompt and receives
   * `receipt: null`.
   *
   * Setting `after` on the prior call together with `before` on this call is
   * double-wait — the engine errors at flow construction.
   *
   * **Self-bound your wait.** The engine does not time out callbacks — a
   * predicate that hangs hangs the flow. Use `waitForConfirmations(n)` or
   * compose a builder that sets its own deadline and honours `ctx.signal`.
   * See rule 6 on `CallRequest`.
   */
  readonly before?: (ctx: WaitContext) => Promise<void>
  /**
   * Awaited after this call's receipt is fetched, before advancing to the
   * next call (or before finalising the flow if this is the last call).
   * Receives this call's receipt. Presence of `after` makes this call the
   * last call of its atomic-batch unit.
   *
   * Setting `after` on this call together with `before` on the next call is
   * double-wait — the engine errors at flow construction.
   *
   * **Self-bound your wait.** The engine does not time out callbacks — a
   * predicate that hangs hangs the flow. Use `waitForConfirmations(n)` or
   * compose a builder that sets its own deadline and honours `ctx.signal`.
   * See rule 6 on `CallRequest`.
   */
  readonly after?: (ctx: WaitContext) => Promise<void>
}

/** App-wide gas handling for every call in the flow. A call's own `gasBuffer` wins over `buffer`. */
export type ActionFlowGasPolicy = {
  readonly buffer?: CallRequest['gasBuffer']
  /** `send` submits with wallet-managed gas when the estimate reverts; for apps that simulate before Execute. Default `throw`. */
  readonly onRevertedEstimate?: 'throw' | 'send'
}

/**
 * Opt-in calldata attribution for a flow. When set, the engine appends a
 * recoverable 8-byte suffix (4-byte client timestamp + `originTag`) to **every**
 * outer call, on both the single-tx and atomic-batch paths — so attribution
 * doesn't depend on how wallet capabilities group calls at runtime.
 *
 * Only set this when every tagged call's target tolerates trailing calldata: it
 * must ABI-decode its args without branching on raw `msg.data`, and must not
 * re-hash or re-commit the calldata. That last case is easy to miss — e.g.
 * Morpho Bundler3 checks a `reenterHash` against `keccak256(msg.data[4:])`,
 * which a suffix silently diverges, reverting on-chain only. The full
 * suffix-safety checklist (Permit2 trailing sigs, EIP-2771 forwarders,
 * calldata-sensitive proxies, …) is in ADR-2026-05-27-calldata-attribution-tagging;
 * when in doubt, omit the field and stay untagged. A mistakenly tagged unsafe
 * target fails closed at `estimateGas` (suspect attribution if a newly opted-in
 * flow starts failing gas estimation).
 *
 * `originTag` must be a 0x-prefixed 4-byte `Hex`. It's a static, app-authored
 * value, so an invalid tag is a misconfiguration: the engine throws in dev/test
 * and drops attribution in production (never blocks the flow). Apps reading it
 * from an env var should also validate at the env boundary via `isValidOriginTag`.
 */
export type ActionFlowAttribution = {
  readonly originTag: Hex
}

// Requests should be executed in the order of their respective arrays, with all signature requests executed before call requests
// Recomended to create via helper function createImmutableActionFlow to ensure runtime immutability
export interface ActionFlow {
  readonly chainId: number // Calls must all be on the same chain (otherwise atomic batching becomes much less useful and more complex)
  readonly label: string
  /** Stable machine identifier for observability; label is the human-readable copy. */
  readonly tag?: string
  readonly signatureRequests: readonly SignatureRequest[]
  readonly callRequests: readonly CallRequest[] // DO NOT assume these will be executed atomically
  /** Account the flow's calls were built for (onBehalf/maker/taker/receiver); execution aborts on signer drift (MORP3-16). */
  readonly expectedAccountAddress?: Address
  readonly reset?: () => void // Optional callback to reset any state that the flow definition creation process stores (ex. signatures)
  readonly onSuccess?: (result: ActionFlowResult) => void | Promise<void> // Optional callback to execute after all requests have been executed successfully
  readonly attribution?: ActionFlowAttribution // Optional opt-in calldata attribution; see ActionFlowAttribution
}

////
// Internal types
////

// Error type for action flow errors which are meant to be user facing.
// `reportable` errors (e.g. chain-switch failure) are still sent to observability;
// non-reportable ones (e.g. user-initiated abort) are silently swallowed.
// `errorType` lets the catch branch in ActionFlowProvider tag observability with
// a per-instance label instead of hardcoding one — so adding a new reportable
// throw site doesn't silently mislabel its events as a previous error type.
export type ActionFlowErrorType =
  | 'transaction-revert'
  | 'user-rejected'
  | 'execution-error'
  | 'chain-switch-error'
  | 'chain-read-error'
  | 'safe-replaced'
  | 'safe-proposal-dropped'
  | 'safe-proposal-queued'
  | 'indeterminate-submission'

export class ActionFlowError extends Error {
  readonly reportable: boolean
  readonly errorType: ActionFlowErrorType
  constructor(
    message: string,
    {
      reportable = true,
      errorType = 'execution-error',
      cause
    }: {
      reportable?: boolean
      errorType?: ActionFlowErrorType
      cause?: unknown
    } = {}
  ) {
    super(message, { cause })
    this.name = 'ActionFlowError'
    this.reportable = reportable
    this.errorType = errorType
  }
}

// Debug metadata attached to EIP-5792 batchTx failures.
export type DebugBundle = {
  // Bundle id returned by the wallet from `wallet_sendCalls` (EIP-5792).
  // Empty / obviously-fake ids signal a wallet bug — e.g. a silent reject that
  // returns a synthetic id which then 404s on `wallet_getCallsStatus`.
  bundleId: string
  // Time between `wallet_sendCalls` resolving and `wallet_getCallsStatus`
  // failing. Short = race; long = backend propagation lag.
  sendCallsToFailureMs: number
}

// Wraps a viem error from `wallet_sendCalls` / `wallet_getCallsStatus` so the
// action-flow runner can forward debug metadata to observability surfaces.
export class BatchTxBundleError extends Error {
  readonly debug: DebugBundle
  /** The wallet reported the batch failed without a receipt, so it never landed. */
  readonly notIncluded: boolean
  constructor(
    message: string,
    {
      debug,
      cause,
      notIncluded = false
    }: { debug: DebugBundle; cause: unknown; notIncluded?: boolean }
  ) {
    super(message, { cause })
    this.name = 'BatchTxBundleError'
    this.debug = debug
    this.notIncluded = notIncluded
  }
}

export type ActionFlowState = { stepIndex: number } &
  // `isRetryBlocked`: the last submission may still land (e.g. a queued Safe proposal), so
  // executing again could send it twice. Cleared by `reset()` or `setActionFlow(null)`, not by
  // replacing the flow: consumers rebuild the same flow whenever their inputs refetch.
  (
    | { flowState: 'idle'; errorMessage?: string; isRetryBlocked?: boolean }
    | {
        flowState: 'execution'
        stepState: 'pending-wallet' | 'pending-transaction' | 'finalizing'
        pendingTxHash?: Hex
        // Set while the step's Safe proposal waits in the Safe queue, which has no deadline.
        pendingSafeTxHash?: Hex
      }
    | { flowState: 'success'; txHash: Hex | null } // null iff didn't execute a tx in flow
    | { flowState: 'reverted'; txHash: Hex | null; errorName?: string }
  )

export type DispatchAction =
  | { type: 'START_EXECUTION' }
  | { type: 'PENDING_TX'; txHash?: Hex; safeTxHash?: Hex }
  | { type: 'FINALIZING' }
  | { type: 'COMPLETE_STEP' }
  | { type: 'ERROR'; errorMessage: string; isRetryBlocked?: boolean }
  | { type: 'SUCCESS'; txHash: Hex | null }
  | { type: 'REVERT'; txHash: Hex | null; errorName?: string }
  | { type: 'RESET' }
  | { type: 'USER_REJECTED' }
  | { type: 'CLEAR_FLOW' }

export type BatchRequest = {
  label: string
  /**
   * The contiguous call requests this batch represents, in execution order.
   * Carried for `before` / `after` callback dispatch — the engine fires the
   * lead call's `before` once before the batch's wallet popup, and the tail
   * call's `after` once after the batch's receipt is fetched.
   */
  callRequests: readonly CallRequest[]
  getCalls: CallRequest['getCall'][]
}

export type Step =
  | (SignatureRequest & { type: 'signature' })
  | (CallRequest & { type: 'singleTx' })
  | (BatchRequest & { type: 'batchTx' })

////
// Lifecycle events
////

/**
 * Core product-analytics events. Terminal outcomes map to:
 * - on-chain revert → `COMPLETED` with `status: 'reverted'`
 * - wallet, bundle, or other execution failure → `ERROR`
 * - user rejection → `USER_REJECTED`
 * Structured logs mirror this mapping as `action_flow.completed` / `action_flow.error`;
 * safe-expired is its own PostHog event but logs as `action_flow.error` at warn.
 */
export const ACTION_FLOW_EVENTS = {
  STARTED: 'action_flow_started',
  COMPLETED: 'action_flow_completed',
  ERROR: 'action_flow_error',
  USER_REJECTED: 'action_flow_user_rejected',
  SAFE_EXPIRED: 'action_flow_safe_expired',
  SAFE_REPLACED: 'action_flow_safe_replaced',
  // A Safe proposal tracked by ActionFlow was rejected, deleted or replaced before execution.
  SAFE_PROPOSAL_DROPPED: 'action_flow_safe_proposal_dropped',
  CALL_BEFORE_STARTED: 'action_flow_call_before_started',
  CALL_BEFORE_SUCCEEDED: 'action_flow_call_before_succeeded',
  CALL_BEFORE_FAILED: 'action_flow_call_before_failed',
  CALL_AFTER_STARTED: 'action_flow_call_after_started',
  CALL_AFTER_SUCCEEDED: 'action_flow_call_after_succeeded',
  CALL_AFTER_FAILED: 'action_flow_call_after_failed',
  // Emitted when a flow opted into calldata attribution but the suffix could not
  // be applied to a call at runtime because its calldata can't safely carry it
  // (e.g. shorter than a 4-byte selector). Gives analytics a count of the
  // attribution coverage gap so on-chain calculations can account for it.
  // Invalid origin tags do not reach here — they fail fast at resolve time.
  ATTRIBUTION_SKIPPED: 'action_flow_attribution_skipped'
} as const

export type CallDetails =
  | {
      call: ResolvedCall
    }
  | {
      calls: ResolvedCall[]
    }
  | { signaturePayload: unknown }
  | Record<string, never>
